export type AdActionType =
  | "increase_budget"
  | "decrease_budget"
  | "increase_bid"
  | "decrease_bid"
  | "pause_campaign"
  | "add_keyword"
  | "add_negative"
  | "placement_adjustment";

export type KeywordTier =
  | "核心词"
  | "1级大词"
  | "2级大词"
  | "1级长尾词"
  | "2级长尾词"
  | "品牌词"
  | "活动词"
  | "竞品ASIN"
  | "其他";

export type GuardrailStatus = "auto_safe" | "operator_can_approve" | "needs_human_review";

export interface AdActionEvidence {
  id?: string;
  asin?: string;
  campaignName?: string;
  adGroupName?: string;
  targetText: string;
  actionType: AdActionType;
  keywordTier: KeywordTier;
  changePercent: number;
  beforeValue?: string;
  afterValue?: string;
  recent30dOrders: number;
  historicalOrders: number;
  recent30dSpend?: number;
  historicalPeakOrders?: number;
  outcomeOrdersDelta?: number;
  outcomeAcosDelta?: number;
}

export interface GuardrailResult {
  status: GuardrailStatus;
  riskLevel: "low" | "medium" | "high";
  requiresOperatorReview: boolean;
  requiresAdminNotice: boolean;
  basisWindowDays: 30;
  reason: string;
  reviewerPrompt?: string;
}

export interface ImpactReviewInput {
  previousOrders: number;
  currentOrders: number;
  previousSales: number;
  currentSales: number;
  actions: AdActionEvidence[];
}

export interface ImpactReview {
  direction: "growth" | "decline" | "flat";
  orderDelta: number;
  orderDeltaPct: number;
  salesDelta: number;
  salesDeltaPct: number;
  summary: string;
  primaryDrivers: AdActionEvidence[];
  positiveSignals: AdActionEvidence[];
  negativeSignals: AdActionEvidence[];
}

export interface KnowledgeItem {
  sourceActionId?: string;
  title: string;
  scope: string;
  confidence: "medium" | "high";
  reuseRule: string;
  nextTimeGuardrail: string;
}

const highValueTiers = new Set<KeywordTier>(["核心词", "1级大词", "品牌词", "活动词"]);
const downwardActions = new Set<AdActionType>(["decrease_budget", "decrease_bid", "pause_campaign"]);

export function evaluateAdjustmentGuardrail(action: AdActionEvidence): GuardrailResult {
  const isDownward = downwardActions.has(action.actionType) && action.changePercent < 0;
  const isHighValueTier = highValueTiers.has(action.keywordTier);
  const isHistoricallyStrong =
    action.historicalOrders >= 12 ||
    action.recent30dOrders >= 5 ||
    (action.historicalPeakOrders ?? 0) >= 8;
  const isLargeCut = Math.abs(action.changePercent) >= 20 || action.actionType === "pause_campaign";

  if (isDownward && (isHighValueTier || isHistoricallyStrong || isLargeCut)) {
    const reasons = [
      isHighValueTier ? `${action.keywordTier}不可直接下调` : "",
      isHistoricallyStrong ? "历史强词/强活动有出单贡献" : "",
      isLargeCut ? "下调幅度达到高风险阈值" : ""
    ].filter(Boolean);

    return {
      status: "needs_human_review",
      riskLevel: "high",
      requiresOperatorReview: true,
      requiresAdminNotice: true,
      basisWindowDays: 30,
      reason: reasons.join("；"),
      reviewerPrompt: `请运营说明为什么要下调 ${action.targetText}，并给管理员展示近30天与历史订单依据、回滚触发条件。`
    };
  }

  if (isDownward && Math.abs(action.changePercent) >= 10) {
    return {
      status: "operator_can_approve",
      riskLevel: "medium",
      requiresOperatorReview: true,
      requiresAdminNotice: false,
      basisWindowDays: 30,
      reason: "中等幅度下调，运营可审核并留痕。"
    };
  }

  return {
    status: "operator_can_approve",
    riskLevel: "low",
    requiresOperatorReview: false,
    requiresAdminNotice: false,
    basisWindowDays: 30,
    reason: "低风险动作，可进入运营确认队列。"
  };
}

export function buildImpactReview(input: ImpactReviewInput): ImpactReview {
  const orderDelta = input.currentOrders - input.previousOrders;
  const salesDelta = input.currentSales - input.previousSales;
  const orderDeltaPct = pct(orderDelta, input.previousOrders);
  const salesDeltaPct = pct(salesDelta, input.previousSales);
  const direction = orderDelta <= -3 ? "decline" : orderDelta >= 3 ? "growth" : "flat";
  const negativeSignals = input.actions
    .filter((action) => (action.outcomeOrdersDelta ?? 0) < 0)
    .sort((left, right) => (left.outcomeOrdersDelta ?? 0) - (right.outcomeOrdersDelta ?? 0));
  const positiveSignals = input.actions
    .filter((action) => (action.outcomeOrdersDelta ?? 0) > 0)
    .sort((left, right) => (right.outcomeOrdersDelta ?? 0) - (left.outcomeOrdersDelta ?? 0));
  const primaryDrivers = direction === "decline" ? negativeSignals.slice(0, 3) : positiveSignals.slice(0, 3);

  return {
    direction,
    orderDelta,
    orderDeltaPct,
    salesDelta,
    salesDeltaPct,
    primaryDrivers,
    positiveSignals,
    negativeSignals,
    summary: buildSummary(direction, orderDelta, orderDeltaPct, primaryDrivers)
  };
}

export function buildKnowledgeItems(actions: AdActionEvidence[]): KnowledgeItem[] {
  return actions
    .filter((action) => (action.outcomeOrdersDelta ?? 0) >= 3 && (action.outcomeAcosDelta ?? 1) <= 0)
    .map((action) => ({
      sourceActionId: action.id,
      title: `${action.keywordTier}有效动作：${labelAction(action.actionType)} ${action.targetText}`,
      scope: `${action.asin ?? "ASIN"} / ${action.campaignName ?? "Campaign"} / ${action.adGroupName ?? "AdGroup"}`,
      confidence: (action.outcomeOrdersDelta ?? 0) >= 8 ? "high" : "medium",
      reuseRule: `下次复用：同系列、同站点、同${action.keywordTier}，近30天有转化且ACOS改善时，可参考 ${action.changePercent}% 的动作幅度。`,
      nextTimeGuardrail: downwardActions.has(action.actionType)
        ? "如为下调动作，仍需检查核心词/历史强词护栏。"
        : "放量前检查库存、利润、Listing转化和预算上限。"
    }));
}

function buildSummary(direction: ImpactReview["direction"], orderDelta: number, orderDeltaPct: number, drivers: AdActionEvidence[]) {
  const deltaText = `${orderDelta > 0 ? "+" : ""}${orderDelta}单，${orderDeltaPct > 0 ? "+" : ""}${orderDeltaPct.toFixed(1)}%`;
  if (direction === "decline") {
    const driver = drivers[0];
    return driver
      ? `单量下滑 ${deltaText}，优先复盘 ${driver.asin ?? ""} ${driver.campaignName ?? ""} 对 ${driver.targetText} 的 ${labelAction(driver.actionType)}。`
      : `单量下滑 ${deltaText}，暂无可归因动作，需要检查库存、价格、竞品和自然排名。`;
  }
  if (direction === "growth") {
    const driver = drivers[0];
    return driver
      ? `单量上涨 ${deltaText}，主要正向动作来自 ${driver.targetText} 的 ${labelAction(driver.actionType)}。`
      : `单量上涨 ${deltaText}，暂无明确广告动作，需要继续沉淀自然排名和外部流量证据。`;
  }
  return `单量基本持平 ${deltaText}，继续观察广告动作与自然流量变化。`;
}

function labelAction(actionType: AdActionType) {
  const labels: Record<AdActionType, string> = {
    increase_budget: "提高预算",
    decrease_budget: "降低预算",
    increase_bid: "提高竞价",
    decrease_bid: "降低竞价",
    pause_campaign: "暂停活动",
    add_keyword: "加词",
    add_negative: "否词/否ASIN",
    placement_adjustment: "广告位调整"
  };
  return labels[actionType];
}

function pct(delta: number, base: number) {
  if (!base) return delta ? 100 : 0;
  return (delta / base) * 100;
}
